import { LinkedinLogo } from "@phosphor-icons/react";
import { useMemo } from "react";

import { PaginatedDataTable } from "common/components/DataTable";
import usePaginatedDataTable from "common/components/DataTable/usePaginatedDataTable";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { typedObjectEntries } from "common/helpers/utils";

import { CREATED_CAMPAIGN_AUDIENCE_COLUMNS } from "../../../../../constants";
import AudienceTargetActions from "./AudienceTargetActions";
import ManageDesktopAudience from "./ManageDesktopAudience";

import type { ColumnDef, VisibilityState } from "@tanstack/react-table";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import type { AudienceRow } from "../../../../../types";

const LOADING_SKELETON_CLASSES: Record<
  keyof typeof CREATED_CAMPAIGN_AUDIENCE_COLUMNS,
  string
> = {
  occupation: "h-4 w-24",
  assignedTo: "h-6 w-24 rounded-full",
  state: "h-6 w-20",
  currentCompanies: "h-4 w-32",
  currentCompaniesTime: "h-4 w-32",
  pastCompanies: "h-4 w-32",
  pastCompaniesTime: "h-4 w-32",
  location: "h-4 w-24",
};

interface AudienceDesktopTableProps {
  rows: AudienceRow[];
  isLoading: boolean;
  selectedTargets: AudienceRow[];
  setSelectedTargets: Dispatch<SetStateAction<AudienceRow[]>>;
  openLeadPreview: (leadId: string) => void;
  emptyStateComponent: ReactNode;
  columnVisibility: VisibilityState;
  setColumnVisibility: (columnVisibility: VisibilityState) => void;
}

export default function AudienceDesktopTable({
  rows,
  isLoading,
  selectedTargets,
  setSelectedTargets,
  openLeadPreview,
  emptyStateComponent,
  columnVisibility,
  setColumnVisibility,
}: AudienceDesktopTableProps) {
  const { permissions } = useSelectedWorkspaceContext();
  const canOpenLeadPreview = permissions.includes("leads_full");

  const columns = useMemo<ColumnDef<AudienceRow>[]>(
    () => [
      {
        id: "target",
        header: "Targets",
        cell: ({ row: { original } }) => {
          const {
            picture_url: pictureUrl,
            full_name: fullName,
            premium,
          } = original;

          return (
            <div className="flex items-center gap-x-2.5">
              {isLoading ? (
                <Skeleton className="size-10 rounded-full" />
              ) : (
                <ProfileImage src={pictureUrl} />
              )}
              <div className="flex flex-col gap-y-0.5">
                {isLoading ? (
                  <>
                    <Skeleton className="h-5 w-28" />
                    <Skeleton className="h-4 w-44" />
                  </>
                ) : (
                  <div className="flex items-center gap-x-1">
                    <span className="line-clamp-1 break-all text-xs-bold">
                      {fullName}
                    </span>
                    {premium && (
                      <LinkedinLogo
                        className="size-4 text-yellow-500"
                        weight="fill"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        },
        enableHiding: false,
      },
      ...typedObjectEntries(CREATED_CAMPAIGN_AUDIENCE_COLUMNS).map(
        ([id, { label, icon, renderTag }]): ColumnDef<AudienceRow> => ({
          id,
          header: label,
          cell: ({ row: { original } }) => {
            if (isLoading) {
              return <Skeleton className={LOADING_SKELETON_CLASSES[id]} />;
            }
            return renderTag(original, true);
          },
          meta: { icon, className: "min-w-40 max-w-40" },
        }),
      ),
      {
        id: "action",
        cell: ({ row: { original } }) => {
          if (isLoading) {
            return <Skeleton className="inline-flex size-8 rounded-full" />;
          }

          return <AudienceTargetActions profile={original} />;
        },
        enableHiding: false,
        meta: { className: "w-0", isSticky: true },
      },
    ],
    [isLoading],
  );

  const { table } = usePaginatedDataTable({
    dataTableOptions: {
      localStorageId: "campaign-details-audience",
      selectedRows: selectedTargets,
      onRowSelectionChange: permissions.includes("campaigns_full")
        ? setSelectedTargets
        : undefined,
      columnVisibility,
      onColumnVisibilityChange: setColumnVisibility,
    },
    reactTableOptions: { data: rows, columns },
    isLoading,
  });

  function onRowClick(row: AudienceRow) {
    if (!canOpenLeadPreview) {
      return;
    }

    openLeadPreview(row.id);
  }

  if (!isLoading && !rows.length) {
    return emptyStateComponent;
  }

  return (
    <PaginatedDataTable
      table={table}
      onRowClick={onRowClick}
      isLoading={isLoading}
      selectionActionsComponent={
        <ManageDesktopAudience
          selectedTargets={selectedTargets}
          resetSelectedTargets={() => setSelectedTargets([])}
        />
      }
    />
  );
}
